import { ConfigProvider } from 'antd';
import { useMemo, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { antdThemeFor } from './antdTheme';
import { cssVariablesForUi, type ThemeMode } from './tokens';
import { DASHBOARD_PALETTE } from './ThemeProvider';
import { cn } from '@/lib/utils';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  RENDERS A SUBTREE IN AN EXPLICIT THEME, independent of the app's own mode.
 *
 *  This is what lets `/design` show both themes on ONE page, which is the point of a
 *  review surface — a gallery you have to toggle to compare is a gallery nobody compares.
 *
 *  Two halves, and both are needed:
 *
 *   • OUR tokens are written as INLINE custom properties through the `style` prop. No
 *     effect, no flash, and jsdom can assert them (`el.style.getPropertyValue`), unlike
 *     anything that depends on the cascade.
 *
 *   • ANTD's are scoped by `cssVar: { key }`. Verified in @ant-design/cssinjs 1.24.0:
 *     `serializeCSSVar` emits `.<key>{…}` — a CLASS, not `:root` — and every antd
 *     component stamps that class onto its own root element. Nested ConfigProviders
 *     already receive distinct keys from `React.useId()`; naming ours makes the selector
 *     readable and lets a test assert it. `inherit: false` stops the parent provider's
 *     tokens leaking in.
 *
 *  ─── Three limitations, stated rather than discovered ──────────────────────────────
 *
 *  1. PORTALLED CONTENT ESCAPES THE SCOPE FOR OUR VARIABLES. AntD's own variables are
 *     fine (the portalled node carries the cssVar class), but `--background`, `--ring` and
 *     every Tailwind/shadcn class inside a portalled Modal resolve against <html>.
 *     `getPopupContainer` below fixes Select, DatePicker, Dropdown, Tooltip and TreeSelect.
 *     Modal and Drawer need a per-instance `getContainer` — ConfigProvider has no global
 *     for those.
 *
 *  2. `dark:` UTILITIES CANNOT BE SCOPED, and are therefore banned by ESLint. The variant
 *     is `&:where(.dark, .dark *)`, a descendant match, so a light scope inside a dark app
 *     still matches it. "Nearest ancestor wins" is not expressible with descendant
 *     combinators. Nothing in the app needs `dark:` — every colour is a variable that this
 *     component overrides — so the ban costs nothing and closes the trap.
 *
 *  3. ANTD STATICS (`message`, `notification`, `Modal.confirm`) always use the ROOT theme:
 *     they mount under the top-level `<App>`. Acceptable; a gallery should not fire toasts.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export type ThemeScopeProps = {
  mode: ThemeMode;
  className?: string;
  children: ReactNode;
};

export const ThemeScope = ({ mode, className, children }: ThemeScopeProps) => {
  const ref = useRef<HTMLDivElement>(null);
  /*
   * ⚠️ [C1h §2] THE PALETTE FOLLOWS THE APP; ONLY THE MODE IS A PROP.
   *
   * This component exists so `/design` can show light and dark on one page — a THEME
   * question. [amended LIGHT-COLOURFUL] The app has one palette, `DASHBOARD_PALETTE`, so
   * there is deliberately no `palette` prop: a gallery showing a palette nobody can see would
   * be a second answer to "which palette is this" that nothing keeps in step.
   */

  // React accepts `--x` keys in a style object at runtime; the cast is what tells
  // TypeScript that, since CSSProperties has no index signature for custom properties.
  const style = useMemo<CSSProperties>(
    () => ({ ...cssVariablesForUi(DASHBOARD_PALETTE, mode), colorScheme: mode }),
    [mode],
  );

  const theme = useMemo(
    () => ({ ...antdThemeFor(mode, DASHBOARD_PALETTE), cssVar: { key: `pams-${mode}` }, inherit: false }),
    [mode],
  );

  return (
    <ConfigProvider theme={theme} getPopupContainer={() => ref.current ?? document.body}>
      <div
        ref={ref}
        data-theme-scope={mode}
        // `dark` is still applied: shadcn's own `.dark` selectors and any third-party CSS
        // keyed on it should agree with the variables written above.
        className={cn(mode === 'dark' && 'dark', 'relative bg-background text-foreground', className)}
        style={style}
      >
        {children}
      </div>
    </ConfigProvider>
  );
};
